"use client";

import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const subjectOptions = [
  "General Inquiry",
  "Order Support",
  "Product Question",
  "Wholesale/Partnerships",
  "Other",
];

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div style={{ backgroundColor: "#F6F5F2" }}>
      <Header />
      <main>
        {/* Page Header */}
        <div
          className="py-20 border-b"
          style={{ borderColor: "#E0DFDB", backgroundColor: "#F6F5F2" }}
        >
          <div className="max-w-container mx-auto px-6 lg:px-8">
            <span className="section-label mb-4 block">Contact</span>
            <h1
              className="font-serif font-bold mb-4"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
                letterSpacing: "-0.03em",
                lineHeight: "1.0",
                color: "#1F1F1F",
              }}
            >
              Get in{" "}
              <em style={{ color: "#3F5A45" }}>Touch</em>
            </h1>
            <p
              className="text-lg max-w-xl"
              style={{
                color: "#6B6B6B",
                fontFamily: "Inter, system-ui, sans-serif",
                lineHeight: "1.6",
              }}
            >
              Have a question about our products, your order, or kratom in general? We&apos;re here to help.
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="py-20">
          <div className="max-w-container mx-auto px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              {submitted ? (
                <div
                  className="rounded-2xl p-10 text-center border"
                  style={{ backgroundColor: "#fff", borderColor: "#E0DFDB" }}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ backgroundColor: "#EAF0EB" }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="#3F5A45"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2
                    className="font-serif font-bold mb-3"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.75rem",
                      color: "#1F1F1F",
                    }}
                  >
                    Message Sent!
                  </h2>
                  <p
                    className="text-base"
                    style={{
                      color: "#6B6B6B",
                      fontFamily: "Inter, system-ui, sans-serif",
                      lineHeight: "1.6",
                    }}
                  >
                    Thanks for reaching out. We typically respond within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-2xl p-8 md:p-10 border"
                  style={{ backgroundColor: "#fff", borderColor: "#E0DFDB" }}
                >
                  {/* Name */}
                  <div className="mb-6">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium mb-2"
                      style={{
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                      style={{
                        border: "1px solid #E0DFDB",
                        backgroundColor: "#F6F5F2",
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#3F5A45")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#E0DFDB")}
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-6">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium mb-2"
                      style={{
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                      style={{
                        border: "1px solid #E0DFDB",
                        backgroundColor: "#F6F5F2",
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#3F5A45")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#E0DFDB")}
                    />
                  </div>

                  {/* Subject */}
                  <div className="mb-6">
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium mb-2"
                      style={{
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all appearance-none"
                      style={{
                        border: "1px solid #E0DFDB",
                        backgroundColor: "#F6F5F2",
                        color: form.subject ? "#1F1F1F" : "#9B9B9B",
                        fontFamily: "Inter, system-ui, sans-serif",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%236B6B6B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 14px center",
                        paddingRight: "40px",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#3F5A45")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#E0DFDB")}
                    >
                      <option value="" disabled>
                        Select a subject
                      </option>
                      {subjectOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div className="mb-8">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium mb-2"
                      style={{
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help..."
                      className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all resize-none"
                      style={{
                        border: "1px solid #E0DFDB",
                        backgroundColor: "#F6F5F2",
                        color: "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                        lineHeight: "1.6",
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#3F5A45")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#E0DFDB")}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: "#3F5A45",
                      fontFamily: "Inter, system-ui, sans-serif",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Send Message
                  </button>

                  {/* Direct email */}
                  <p
                    className="text-sm text-center mt-6"
                    style={{
                      color: "#6B6B6B",
                      fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    Or email us directly at{" "}
                    <a
                      href="mailto:support@kratapillar.com"
                      className="font-medium underline underline-offset-2 transition-colors hover:opacity-80"
                      style={{ color: "#3F5A45" }}
                    >
                      support@kratapillar.com
                    </a>
                  </p>
                  <p
                    className="text-xs text-center mt-2"
                    style={{
                      color: "#9B9B9B",
                      fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    We typically respond within 24 hours.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
